<template>
    <div 
        :id="setId()" 
        role="dialog"
        ref="videoWrap" 
        aria-label="Video Player" 
        :class="{
            'video-fullscreen': 1,
            'is-fixed': !video.includes('Award'),
        }"
        @keydown.esc="() => $refs.videoClose.click()"
    >
        <span id="video-desc" class="sr-only" v-html="getVideoLabel( name )" />
        
        <button aria-label="Close video" ref="videoClose" :id="setId('close')" @click.prevent="closeVideo">
            <svg role="presentation" focusable="false" viewBox="0 0 40 40">
                <path d="M 10,10 L 30,30 M 30,10 L 10,30" />
            </svg>
        </button>
        <button 
            :id="setId('pause')"
            ref="pause"
            type="button" 
            :aria-label="paused ? `Play video` : `Pause video`" 
            :tabindex="paused ? undefined : -1"
            @click="toggleVideo"
        >
            <svg v-if="!paused" role="presentation" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 70 70" width="64px" height="64px">
                <path d="M52.5,0c-4.972,0-9,1.529-9,6.5v57c0,4.971,4.028,6.5,9,6.5c4.971,0,9-1.529,9-6.5v-57     C61.5,1.529,57.471,0,52.5,0z" fill="#FFFFFF"/>
                <path d="M17.5,0c-4.972,0-9,1.529-9,6.5v57c0,4.971,4.028,6.5,9,6.5c4.971,0,9-1.529,9-6.5v-57     C26.5,1.529,22.471,0,17.5,0z" fill="#FFFFFF"/>
            </svg>
            <svg v-else role="presentation" focusable="false" xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 373.008 373.008">
                <path d="M61.792,2.588C64.771,0.864,68.105,0,71.444,0c3.33,0,6.663,0.864,9.655,2.588l230.116,167.2    c5.963,3.445,9.656,9.823,9.656,16.719c0,6.895-3.683,13.272-9.656,16.713L81.099,370.427c-5.972,3.441-13.334,3.441-19.302,0    c-5.973-3.453-9.66-9.833-9.66-16.724V19.305C52.137,12.413,55.818,6.036,61.792,2.588z" fill="#fff"/>
            </svg>
        </button>
        <video ref="video" aria-labelledby="video-desc" :id="setId('video')" preload playsinline>
            <source :src="video" type="video/mp4">
            <track v-if="transcript" :src="transcript" default kind="captions" srclang="en" label="English">
        </video>
    </div>
</template>

<style lang="scss" scoped>
div {
    max-height: 100dvh;
    pointer-events: none;
    position: absolute;
    overflow: hidden;
    opacity: 0;
    top: 0; left: 0;
    bottom: 0; right: 0;
    z-index: 999;
    transition: opacity 222ms ease-out;
    background: #000;
    &.is-fixed {
        position: fixed;
    }
    video {
        width: 100%;
        height: 100%;
        position: absolute;
        left: 50%; top: 50%;
        z-index: 10;
        object-fit: contain;
        transform: translate3d(-50%, -50%, 0)
    }
    button {
        $s: 18px;
        display: none;
        position: absolute;
        background: none;
        box-shadow: none;
        top: calc(var(--space-side, 24px) - #{$s*1.25/2});
        left: $s*1.5;
        padding: $s*1.25;
        z-index: 20;
        cursor: pointer;
        border-radius: 9em;

        &:focus-visible {
            outline-offset: -1em;
            outline: 2px dashed #fff;
            mix-blend-mode: difference;
        }

        svg {
            stroke: #fff;
            fill: transparent;
            stroke-linecap: round;
            stroke-width: 3;
            width: 2*$s; height: 2*$s
        }
    }
    button[id*=pause] {
        display: flex;
        position: absolute;
        top: calc(50% - 3em);
        left: calc(50% - 3em);
        width: 6em;
        height: 6em;
        z-index: 20;
        mix-blend-mode: difference;

        svg {
            width: calc(1vmax + 1em);
            margin: auto;
            fill: #fff;
        }
        &[aria-label*=Play]:not(:focus-visible) {
            outline-offset: -1em;
            outline: 1px solid #fff5;
        }
    }

    @media( max-width: 767px ) {
        position: fixed;
        button { 
            $s: 1em;
            padding: 1.25em; 
            top: 1.5em;
            margin: -1em;

            svg {
                display: block;
                width: 1.5*$s; 
                height: 1.5*$s
            }
        }
    }
    &.is-playing {
        opacity: 1;
        button {
            display: inline-block; 
            pointer-events: all 
        }
    }
}
</style>

<script>
import { videos } from '@/mixins/main'

export default {
    name: 'VideoFullscreen',
    props: {
        media: [String, Object],
        name: String,
    },
    mixins: [ videos ],
    data: () => ({
        paused: false,
    }),
    methods: {
        setId( child ) {
            return child ? `${this.name}-${child}` : this.name
        },
        getLabel( text ) {
            return String(text).replace('_', ' ')
        },
        getVideoLabel( name ) {
            if (this.video.includes('Award')) {
                return 'Doane Grant Thornton Best Workplaces awards'
            }
            return this.getLabel(name).concat(' story, English captions available')
        },
        closeVideo() {
            if ( ! this.$refs.video.id.includes('award-') ) {
                this.rootClass('rm', 'no-overflow')
            }
            const { video } = this.$refs
            video.pause()
            video.currentTime = 0
            this.paused = false
        },
        playVideo() {
            this.$refs.video.play()
        },
        pauseVideo() {
            this.$refs.video.pause()
        },
        toggleVideo() {
            this.paused = !this.paused
            this.paused
                ? this.pauseVideo()
                : this.playVideo()
        }
    },
}
</script>
